import { useTheme } from '@mui/material';
import { DataElementId } from '../../../../constants/data-element-tracking-ids';
import {
  EmployeeLoginType,
  LoginMethod,
} from '../../../../selectors/interfaces';
import { useSettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import { useBuildersToProcessGqlData } from '../../../../dotcms/use-builders-to-process-gql-data';
import { useFetchIssuerDotcmsContentWithDefaultFallback } from '../../../../dotcms/use-fetch-from-dotcms-with-default-fallback';
import { GlobalLoadingStateOperation } from '../../../../components/abc/global-loading-context/global-loading-context';
import { LoginFormSsoIssuerGqlHelper } from '../../../../dotcms/dotcms-models/dotcms-login-form-sso-issuer';
import useLoginProviders from './use-login-providers';

export const LoginMethodElementIds: Partial<
Record<LoginMethod, DataElementId>
> = {
  [LoginMethod.SSO]: DataElementId.LOGIN_PAGE_PROVIDER_LOGIN_BUTTON_SSO,
  [LoginMethod.BUSINESS_GROUP]:
    DataElementId.LOGIN_PAGE_PROVIDER_LOGIN_BUTTON_BUSINESS_GROUP,
  [LoginMethod.PASSWORDLESS_EMAIL]:
    DataElementId.LOGIN_PAGE_PROVIDER_LOGIN_BUTTON_PASSWORDLESS_EMAIL,
};

export const useLoginSsoForm = () => {
  const muiTheme = useTheme();
  const { isMobile } = useSettingBasedOnBreakpoints(muiTheme);

  const customDynamicVariableProviderName = 'ce_custom_provider_name';
  const customDynamicVariableProvider = `{{${customDynamicVariableProviderName}}}`;

  const { createGqlQueryForSingle, processGqlDataAsSingle, contentTypeName } =
    useBuildersToProcessGqlData(LoginFormSsoIssuerGqlHelper);
  const { data, loading, error } =
    useFetchIssuerDotcmsContentWithDefaultFallback({
      buildGqlSubQuery: createGqlQueryForSingle,
      processResponse: processGqlDataAsSingle,
      loadingStateOperationName:
        GlobalLoadingStateOperation.LOGIN_FORM_SSO_DOTCMS_FETCH,
      contentTypeName,
      delayedCustomDynamicVariables: [customDynamicVariableProviderName],
    });

  const { theme, loginProviders } = useLoginProviders({
    businessGroupsSeparator: data?.loginMethodBusinessGroupsSeparator ?? '',
  });

  const basicLoginProviders = loginProviders.filter((x) =>
    x.userTypes.includes(EmployeeLoginType.REGULAR));

  const getExtraLoginIfValid = (employeeLoginType: EmployeeLoginType) => {
    const showLoginSeparetly = basicLoginProviders.find(
      (x) => !x.userTypes.includes(employeeLoginType),
    );
    return showLoginSeparetly
      ? loginProviders.find((x) => x.userTypes.includes(employeeLoginType))
      : undefined;
  };

  const extraLoginProviders = {
    formerEmployeeLoginProvider: getExtraLoginIfValid(
      EmployeeLoginType.TERMINATED,
    ),
    adminLoginProvider: getExtraLoginIfValid(EmployeeLoginType.ADMIN),
  };

  return {
    theme,
    isMobile,
    data,
    loading,
    error,
    customDynamicVariableProvider,
    basicLoginProviders,
    extraLoginProviders,
  };
};
